import { useState, useRef, useCallback } from 'react';

interface MediaUploadProps {
  value: string;
  onChange: (url: string) => void;
  onUpload: (file: File) => Promise<string>;
  accept?: string;
  mediaType?: 'image' | 'video' | 'html';
  variant?: 'default' | 'icon';
}

export function MediaUpload({
  value,
  onChange,
  onUpload,
  accept = 'image/*,video/*',
  mediaType = 'image',
  variant = 'default',
}: MediaUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const validateFile = (file: File): string | null => {
    // Check file type
    const acceptedTypes = accept.split(',').map((t) => t.trim());
    const isValidType = acceptedTypes.some((type) => {
      if (type.endsWith('/*')) {
        const category = type.replace('/*', '');
        return file.type.startsWith(category);
      }
      if (type.startsWith('.')) {
        const ext = file.name.toLowerCase().split('.').pop();
        return ext === type.slice(1).toLowerCase();
      }
      return file.type === type;
    });

    if (!isValidType) {
      return 'Invalid file type';
    }

    return null;
  };

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress (since we don't have real progress tracking)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      const url = await onUpload(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        uploadFile(file);
      }
    },
    [onUpload, onChange, accept]
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange('');
    setError(null);
  };

  const isHtml = mediaType === 'html' || value?.match(/\.(html|htm)$/i);
  const isImage = !isHtml && (mediaType === 'image' || value?.match(/\.(jpg|jpeg|png|gif|webp)$/i));

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Upload zone */}
      {!value && (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg text-center cursor-pointer
            transition-colors
            ${
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-border-strong hover:border-border-strong'
            }
            ${isUploading ? 'pointer-events-none opacity-60' : ''}
            ${variant === 'icon' ? 'p-4' : 'p-6'}
          `}
        >
          {isUploading ? (
            <div className="space-y-3">
              <div className="w-8 h-8 mx-auto border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-content-muted">Uploading...</p>
              <div className="w-full max-w-xs mx-auto bg-surface-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              <svg
                className="mx-auto h-12 w-12 text-content-subtle"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="mt-2 text-sm text-content-muted">
                <span className="font-medium text-primary">Click to upload</span>
                {' or drag and drop'}
              </p>
              <p className="mt-1 text-xs text-content-subtle">
                {mediaType === 'video' ? 'MP4' : mediaType === 'html' ? 'HTML' : 'PNG, JPG, GIF'}
              </p>
            </>
          )}
        </div>
      )}

      {/* Preview */}
      {value && variant === 'icon' && (
        <div className="flex items-center gap-4 rounded-xl border border-border bg-surface-muted/30 p-3">
          <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-[20px] border border-border bg-surface-muted shadow-sm">
            {isImage ? (
              <img src={value} alt="App icon preview" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-content-subtle">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 6h16v12H4V6z" />
                </svg>
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-content">Icon preview</p>
            <p className="mt-1 truncate text-xs text-content-subtle" title={value}>{value}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleClick}
                disabled={isUploading}
                className="rounded-md border border-border-strong bg-surface px-3 py-1.5 text-xs font-medium text-content-muted transition-colors hover:bg-surface-muted disabled:opacity-50"
              >
                {isUploading ? 'Uploading…' : 'Replace'}
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={isUploading}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-danger transition-colors hover:bg-danger-soft disabled:opacity-50"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {value && variant === 'default' && (
        <div className="relative rounded-lg border border-border overflow-hidden">
          {isImage ? (
            <img
              src={value}
              alt="Preview"
              className="w-full h-40 object-cover"
            />
          ) : isHtml ? (
            <div className="w-full h-40 bg-surface-muted flex items-center justify-center">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-content-subtle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
                <p className="mt-2 text-sm text-content-subtle">HTML file</p>
              </div>
            </div>
          ) : (
            <video
              src={value}
              controls
              preload="metadata"
              className="w-full h-40 object-contain bg-black"
            />
          )}

          {/* Remove button */}
          <button
            onClick={handleRemove}
            type="button"
            className="absolute top-2 right-2 p-1.5 bg-surface rounded-full shadow-md hover:bg-surface-muted transition-colors"
          >
            <svg
              className="w-4 h-4 text-content-subtle"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* URL display */}
          <div className="p-2 bg-surface-muted text-xs text-content-subtle truncate">
            {value}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-danger">{error}</p>
      )}
    </div>
  );
}
